import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { Theme, useMediaQuery } from "@mui/system";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { LockDetail } from "@pimo/resource-lock-plugin";
import { Initiative, User } from "aint-types";
import dayjs from "dayjs";
import { PropsWithChildren, useEffect } from "react";
import {
  FieldError,
  FormProvider,
  Path,
  useForm,
  useFormContext,
  UseFormSetError,
} from "react-hook-form";

import { validateImpactActuals } from "../../../form/validate-form-fields";
import { DefaultValuesContext } from "./update-impact-default-values-context";
import { UpdateImpactDialogContent } from "./update-impact-dialog-content";

export type UpdateImpactDialogProps = {
  title: string;
  /** Show Loading spinner instead of form*/
  isLoading?: boolean;
  /** Show Loading spinner on top of form*/
  isSaving?: boolean;
  /** The active/currently open initiative to be highlighted in the navigation. */
  activeInitiative?: Initiative;
  /** The initiatives to be shown in the navigation. */
  initiatives?: Initiative[];
  currentProgres?: number;
  initiativesLockDetails: Record<number, LockDetail>;
  currentUser: User;
  switchReportingLock: (
    oldInitiativeId: number,
    newInitiativeId: number
  ) => void;
};

export type UpdateImpactDialogEvents =
  | "report-impact-dialog:submit"
  | "report-impact-dialog:close"
  | "report-impact-dialog:close-with-changes"
  | "report-impact-dialog:navigate-with-changes";

export const UpdateImpactDialog: PimoReactComponent<
  UpdateImpactDialogProps,
  UpdateImpactDialogEvents,
  Initiative
> = (props) => {
  const {
    fireEvent,
    activeInitiative: initiative,
    title,
    initiativesLockDetails,
    currentUser,
    isLoading = false,
    isSaving = false,
    switchReportingLock,
  } = props;
  const formName = "report-impact-dialog-form";

  const formMethods = useForm<Partial<Initiative>>({
    defaultValues: {
      ...initiative,
    },
  });

  useEffect(() => {
    formMethods.reset(initiative);
  }, [formMethods, initiative]);

  const onSubmit = () => {
    const data = formMethods.getValues();
    formMethods.clearErrors();

    const hasError = validate(data as Initiative, formMethods.setError);

    if (hasError) {
      return;
    }

    return fireEvent?.("report-impact-dialog:submit", data as Initiative);
  };

  return (
    <FormProvider {...formMethods}>
      <DefaultValuesContext.Provider value={initiative ?? {}}>
        <DialogWrapper
          title={title}
          formName={formName}
          fireEvent={fireEvent}
          isLoading={isLoading}
          isSaving={isSaving}
          lastSubmitted={initiative?.lastSubmitted}
          currentProgres={props.currentProgres}
        >
          {initiative && (
            <UpdateImpactDialogContent
              formName={formName}
              onSubmit={onSubmit}
              onInvalid={() => {
                onSubmit();
              }}
              initiatives={props.initiatives ?? []}
              activeInitiative={initiative}
              initiativesLockDetails={initiativesLockDetails}
              currentUser={currentUser}
              switchReportingLock={switchReportingLock}
            />
          )}
        </DialogWrapper>
      </DefaultValuesContext.Provider>
    </FormProvider>
  );
};

const DialogHeader = styled(Box)(({ theme }) => ({
  marginLeft: "40px",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(2),
}));

const DialogWrapper = ({
  title,
  formName,
  fireEvent,
  isLoading,
  isSaving,
  children,
  lastSubmitted,
  currentProgres,
}: PropsWithChildren<{
  title: string;
  formName: string;
  fireEvent:
    | ((event: UpdateImpactDialogEvents, payload?: Initiative) => void)
    | undefined;
  isLoading: boolean;
  isSaving: boolean;
  lastSubmitted?: string;
  currentProgres?: number;
}>) => {
  const {
    formState: { dirtyFields },
  } = useFormContext();
  const isLargeScreen = useMediaQuery("(max-width:1000px)");
  const isDirty = Object.keys(dirtyFields).length > 0;
  const handleClose = () => {
    if (isDirty) {
      fireEvent?.("report-impact-dialog:close-with-changes");
      return;
    }

    fireEvent?.("report-impact-dialog:close");
  };

  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <CircularProgress
          size={50}
          sx={{ color: (theme: Theme) => theme.palette.action }}
        />
      </Backdrop>
    );
  }

  return (
    <Dialog
      sx={{
        margin: "50px",
      }}
      PaperProps={{ sx: { borderRadius: "12px" } }}
      fullWidth={true}
      maxWidth={isLargeScreen ? "md" : "lg"}
      fullScreen
      open={true}
      onClose={handleClose}
    >
      {isSaving && (
        <Backdrop sx={{ color: "#fff", zIndex: 99999 }} open={true}>
          <CircularProgress
            size={50}
            sx={{ color: (theme: Theme) => theme.palette.action }}
          />
        </Backdrop>
      )}

      <DialogHeader sx={{ paddingLeft: 0, marginLeft: 3 }}>
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "22px",
            paddingLeft: 0,
          }}
        >
          {title}
          {/* TODO: Add due date */}
        </DialogTitle>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography fontSize="16px">{`Overall Completion: ${currentProgres}%`}</Typography>
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogHeader>
      <DialogContent sx={{ padding: 0 }} dividers={true}>
        {children}
      </DialogContent>
      <DialogActions>
        <UpdateImpactActions
          handleClose={handleClose}
          formName={formName}
          lastSubmitted={lastSubmitted}
        />
      </DialogActions>
    </Dialog>
  );
};

function UpdateImpactActions({
  formName,
  handleClose,
  lastSubmitted,
}: {
  formName: string;
  handleClose: () => void;
  lastSubmitted?: string;
}) {
  return (
    <Box
      display="flex"
      gap="8px"
      justifyContent="flex-end"
      alignItems="center"
      sx={{ padding: "20px 40px", width: "100%" }}
    >
      {lastSubmitted && (
        <Typography fontSize="12px" fontWeight="700" marginRight="12px">
          Last submit: {dayjs(lastSubmitted).format("YYYY/MM/DD")}
        </Typography>
      )}
      <Button
        sx={(theme) => ({
          borderRadius: "4px",
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        })}
        onClick={handleClose}
        variant="outlined"
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        type="submit"
        form={formName}
        sx={{
          borderRadius: "4px",
          backgroundColor: "#F86200",
          color: "#FFFFFF",
        }}
      >
        Save & Submit
      </Button>
    </Box>
  );
}

const validate = (
  data: Initiative,
  setError: UseFormSetError<Partial<Initiative>>
) => {
  const errors = validateImpactActuals(data, true);

  let hasError = false;

  for (const [key, value] of Object.entries(errors ?? {})) {
    if (value && key !== "fieldsWithErrors") {
      hasError = true;
      setError(key as Path<Initiative>, value as FieldError);
    }
  }

  return hasError;
};
